import React, { useState, useEffect } from 'react';

const API_BASE = 'https://custode-production.up.railway.app';

const LEVEL_CONFIG = {
  critical: { color: '#ef4444', bg: '#2d1515', label: 'CRITICAL', emoji: '🚨' },
  warning:  { color: '#f97316', bg: '#2d1f10', label: 'WARNING',  emoji: '⚠️' },
  info:     { color: '#3b82f6', bg: '#101d2d', label: 'INFO',     emoji: 'ℹ️' },
  clean:    { color: '#22c55e', bg: '#0f2318', label: 'CLEAN',    emoji: '✅' },
};

function formatTime(ts) {
  const d = new Date(ts);
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function AlertCard({ alert }) {
  const cfg = LEVEL_CONFIG[alert.alert_level] || LEVEL_CONFIG.warning;
  return (
    <div style={{
      background: cfg.bg,
      border: `1px solid ${cfg.color}33`,
      borderLeft: `4px solid ${cfg.color}`,
      borderRadius: 10,
      padding: '16px 20px',
      marginBottom: 12,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 18 }}>{cfg.emoji}</span>
          <span style={{
            background: cfg.color,
            color: '#fff',
            fontSize: 11,
            fontWeight: 700,
            padding: '2px 8px',
            borderRadius: 4,
            letterSpacing: 1,
          }}>{cfg.label}</span>
          {alert.categories?.map(c => (
            <span key={c} style={{
              background: '#ffffff15',
              color: '#ccc',
              fontSize: 11,
              padding: '2px 8px',
              borderRadius: 4,
            }}>{c}</span>
          ))}
        </div>
        <span style={{ color: '#888', fontSize: 12 }}>
          {alert.message_timestamp ? `Sent: ${formatTime(alert.message_timestamp)}` : formatTime(alert.timestamp)}
        </span>
      </div>
      <p style={{ color: '#e0e0e0', fontSize: 14, lineHeight: 1.5, marginBottom: 8 }}>
        {alert.explanation}
      </p>
      <div style={{ display: 'flex', gap: 16, fontSize: 12, color: '#888', flexWrap: 'wrap' }}>
        {alert.child_name && <span>👦 {alert.child_name}</span>}
        <span>📱 {alert.platform?.toUpperCase()}</span>
        <span>{alert.direction === 'outbound' ? '📤 Sent' : '📥 Received'}{alert.message_timestamp ? ` · ${formatTime(alert.message_timestamp)}` : ''}</span>
        <span>🎯 {Math.round((alert.confidence || 0) * 100)}% confidence</span>
      </div>
    </div>
  );
}

function LoginScreen({ onLogin }) {
  const [childId, setChildId] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/alerts/${childId}`, {
        headers: { 'X-Api-Key': apiKey }
      });
      if (res.ok) {
        localStorage.setItem('custode_child_id', childId);
        localStorage.setItem('custode_api_key', apiKey);
        onLogin(childId, apiKey);
      } else if (res.status === 401) {
        setError('Invalid API key.');
      } else {
        setError('Could not connect. Try again.');
      }
    } catch {
      setError('Network error. Check connection.');
    }
    setLoading(false);
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ width: '100%', maxWidth: 380 }}>
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🛡️</div>
          <h1 style={{ fontSize: 28, fontWeight: 700, color: '#fff' }}>Custode</h1>
          <p style={{ color: '#888', marginTop: 6 }}>The safety net every family needs</p>
        </div>
        <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={{ fontSize: 12, color: '#888', marginBottom: 6, display: 'block', textTransform: 'uppercase', letterSpacing: 1 }}>Child ID</label>
            <input
              value={childId}
              onChange={e => setChildId(e.target.value)}
              placeholder="Child ID"
              required
              style={inputStyle}
            />
          </div>
          <div>
            <label style={{ fontSize: 12, color: '#888', marginBottom: 6, display: 'block', textTransform: 'uppercase', letterSpacing: 1 }}>API Key</label>
            <input
              type="password"
              value={apiKey}
              onChange={e => setApiKey(e.target.value)}
              placeholder="API Key"
              required
              style={inputStyle}
            />
          </div>
          {error && <p style={{ color: '#ef4444', fontSize: 13 }}>{error}</p>}
          <button type="submit" disabled={loading} style={buttonStyle}>
            {loading ? 'Connecting...' : 'View Dashboard'}
          </button>
        </form>
      </div>
    </div>
  );
}

function Dashboard({ childId, apiKey, onLogout }) {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);

  const fetchAlerts = async () => {
    try {
      const res = await fetch(`${API_BASE}/alerts/${childId}`, {
        headers: { 'X-Api-Key': apiKey }
      });
      if (res.ok) {
        const data = await res.json();
        setAlerts(data.alerts || []);
        setLastRefresh(new Date());
      }
    } catch {}
    setLoading(false);
  };

  useEffect(() => {
    fetchAlerts();
    const interval = setInterval(fetchAlerts, 30000); // refresh every 30s
    return () => clearInterval(interval);
  }, []);

  const critical = alerts.filter(a => a.alert_level === 'critical').length;
  const warning  = alerts.filter(a => a.alert_level === 'warning').length;

  return (
    <div style={{ maxWidth: 700, margin: '0 auto', padding: '20px 16px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 24 }}>🛡️</span>
          <div>
            <h1 style={{ fontSize: 20, fontWeight: 700 }}>Custode</h1>
            <p style={{ fontSize: 12, color: '#888' }}>{childId}</p>
          </div>
        </div>
        <button onClick={onLogout} style={{ background: 'none', border: '1px solid #333', color: '#888', padding: '6px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
          Sign out
        </button>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 24 }}>
        <StatCard label="Total Alerts" value={alerts.length} color="#3b82f6" />
        <StatCard label="Critical" value={critical} color="#ef4444" />
        <StatCard label="Warnings" value={warning} color="#f97316" />
      </div>

      {/* Alerts */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h2 style={{ fontSize: 16, fontWeight: 600, color: '#ccc' }}>Alert History</h2>
        {lastRefresh && <span style={{ fontSize: 12, color: '#555' }}>Updated {lastRefresh.toLocaleTimeString()}</span>}
      </div>

      {loading ? (
        <p style={{ color: '#555', textAlign: 'center', padding: 40 }}>Loading alerts...</p>
      ) : alerts.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#555' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
          <p>No alerts detected yet.</p>
          <p style={{ fontSize: 13, marginTop: 6 }}>Custode is monitoring in the background.</p>
        </div>
      ) : (
        alerts.map((alert, i) => <AlertCard key={i} alert={alert} />)
      )}
    </div>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div style={{ background: '#1a1a2e', border: '1px solid #222', borderRadius: 10, padding: '14px 16px', textAlign: 'center' }}>
      <div style={{ fontSize: 28, fontWeight: 700, color }}>{value}</div>
      <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>{label}</div>
    </div>
  );
}

const inputStyle = {
  width: '100%',
  background: '#1a1a2e',
  border: '1px solid #333',
  borderRadius: 8,
  padding: '12px 14px',
  color: '#fff',
  fontSize: 14,
  outline: 'none',
};

const buttonStyle = {
  background: '#3b82f6',
  color: '#fff',
  border: 'none',
  borderRadius: 8,
  padding: '13px',
  fontSize: 15,
  fontWeight: 600,
  cursor: 'pointer',
  marginTop: 4,
};

export default function App() {
  const [auth, setAuth] = useState(() => {
    const childId = localStorage.getItem('custode_child_id');
    const apiKey  = localStorage.getItem('custode_api_key');
    return childId && apiKey ? { childId, apiKey } : null;
  });

  const handleLogin = (childId, apiKey) => setAuth({ childId, apiKey });
  const handleLogout = () => {
    localStorage.removeItem('custode_child_id');
    localStorage.removeItem('custode_api_key');
    setAuth(null);
  };

  if (!auth) return <LoginScreen onLogin={handleLogin} />;
  return <Dashboard childId={auth.childId} apiKey={auth.apiKey} onLogout={handleLogout} />;
}
